import type { QueryClient } from '@tanstack/react-query'
import { fetchHierarchy } from './hierarchy-api.ts'
import type { TreeNode } from './hierarchy-api.ts'
import { listRegistry } from './registry-api.ts'

/**
 * The id -> name lookups and the create form's option lists, fetched once
 * and held apart from the 5-second session poll.
 *
 * This used to be five more awaits inside the /sessions loader: the
 * hierarchy plus four registry lists, refetched on every tick alongside
 * the session page. None of it can plausibly change in 5 seconds — a
 * harness is not renamed while you watch — so that was five requests a
 * tick of pure waste, and waste that grows with the org: the hierarchy
 * response is the whole tree, so the poll gets heavier every time someone
 * adds a project.
 *
 * The cache is the router's `QueryClient`, which is created per router
 * (and therefore per SSR request) in `integrations/tanstack-query`. That
 * matters: a module-level cache would be shared across requests inside a
 * Cloudflare isolate, which for per-tenant hierarchy data is a
 * cross-account leak waiting to happen.
 *
 * Kept in the loader rather than moved into a `useQuery` in the component
 * so names are still present on first paint — the component only renders
 * once the loader resolves, and a page that flashes raw uuids for 100ms
 * on every visit is a visible regression to pay for an invisible win.
 */

export interface ProjectOption {
  id: string
  rootId: string
  label: string
}

export interface FailoverOption {
  id: string
  rootId: string
  name: string
}

export interface SessionLookups {
  projects: ProjectOption[]
  agentDefs: { id: string; label: string }[]
  failoverConfigs: FailoverOption[]
  /** id -> display name, for every project, agent definition, harness and
   * CLI profile the list might reference. */
  names: Record<string, string>
}

/**
 * How long a fetched lookup set is reused.
 *
 * Long enough that the 5-second poll never refetches it, short enough
 * that a rename shows up without a reload. Five minutes is the same order
 * as how often anyone edits the registry.
 */
export const SESSION_LOOKUPS_STALE_MS = 5 * 60_000

export const SESSION_LOOKUPS_QUERY_KEY: readonly string[] = ['session-lookups']

/** An empty set, used when the fetch fails. Names are best-effort: the
 * page still renders, just with raw ids where names would have been. */
function emptyLookups(): SessionLookups {
  return { projects: [], agentDefs: [], failoverConfigs: [], names: {} }
}

/**
 * Flattens the org tree into the launchable project nodes the create form
 * can offer, labelled by their path, and records id -> name for display.
 *
 * A container (no repository resolves) is not offered: nothing can run
 * there, and the API would refuse it with `project_not_launchable`.
 */
function collectProjects(
  node: TreeNode,
  rootId: string,
  path: string[],
  projects: ProjectOption[],
  names: Record<string, string>,
): void {
  const here = [...path, node.name]
  names[node.id] = node.name
  if (node.launchable)
    projects.push({ id: node.id, rootId, label: here.join(' / ') })
  for (const child of node.children) {
    collectProjects(child, rootId, here, projects, names)
  }
}

/**
 * The five requests, as one best-effort call.
 *
 * Swallowing the failure is deliberate and predates this split: the
 * session list is the gated, must-succeed fetch (a 401 there lands on
 * /login), and a registry hiccup must not take the page down with it. The
 * caller gets whatever was collected, so partial names beat no page.
 */
export async function fetchSessionLookups(): Promise<SessionLookups> {
  const lookups = emptyLookups()
  try {
    const [tree, defs, harnesses, profiles, failovers] = await Promise.all([
      fetchHierarchy(),
      listRegistry('agent-definitions'),
      listRegistry('harnesses'),
      listRegistry('cli-profiles'),
      listRegistry('failover-configs'),
    ])
    for (const root of tree.roots) {
      collectProjects(root, root.id, [], lookups.projects, lookups.names)
    }
    for (const def of defs) {
      lookups.names[def.id] = def.name ?? def.id
      lookups.agentDefs.push({ id: def.id, label: def.name ?? def.id })
    }
    for (const row of [...harnesses, ...profiles]) {
      lookups.names[row.id] = row.name ?? row.id
    }
    for (const row of failovers) {
      lookups.failoverConfigs.push({
        id: row.id,
        rootId: row.rootId,
        name: row.name ?? row.id,
      })
    }
  } catch {
    // Best-effort by contract — see the doc comment. Returning what was
    // collected rather than rethrowing is the whole point.
  }
  return lookups
}

/**
 * Fetch-or-reuse against the router's query client.
 *
 * `ensureQueryData` returns cached data without a request while it is
 * fresh, which is what takes these five calls out of the poll. It is also
 * why the stale time is a constant rather than inlined: the poll interval
 * and this have to stay on opposite sides of each other, and a test
 * asserts they are.
 */
export async function ensureSessionLookups(
  queryClient: QueryClient,
): Promise<SessionLookups> {
  return queryClient.ensureQueryData({
    queryKey: SESSION_LOOKUPS_QUERY_KEY,
    queryFn: fetchSessionLookups,
    staleTime: SESSION_LOOKUPS_STALE_MS,
  })
}
